/**
 * The in-run menu (2026-09-02).
 *
 * User: "the only way to exit a match right now is to wait for the automatic
 * ejection. There is no menu and no way to manually quit the game."
 *
 * Exactly right, and worse than it sounds: a player who wanted out had to
 * close the tab, which produced the same outcome as quitting while looking
 * like a crash. There was no pause, no controls reference, and no way to
 * stop playing on purpose.
 *
 * ## Escape opens it, because Escape is already doing half the job
 *
 * The browser owns Escape while the pointer is locked — it releases the mouse
 * and there is no preventing that. So rather than fight it, Escape now means
 * what it means in every other game: the mouse comes back *and* the menu
 * opens. The HUD already told players "Esc releases the mouse"; this makes
 * that gesture lead somewhere.
 *
 * A visible button sits in the corner too, because Escape is not reachable on
 * a phone.
 *
 * ## Abandon asks twice
 *
 * Quitting settles the run as a death — you keep only what is insured. That
 * is the same thing a disconnect has always done, but a player pressing a
 * button deserves to be told what it costs before it happens, so the control
 * swaps into an explicit confirm rather than firing on the first press.
 */

import { LABEL } from './hud-theme';
import { t, N_ } from '../i18n/i18n';

const DEFAULT_CONFIRM = N_('Leaving settles this run as a death. You lose everything you are carrying except what you insured.');

export interface RunMenuHandlers {
  /** Settle the run as abandoned. The menu closes itself first. */
  onAbandon: () => void;
  /**
   * What leaving actually costs, in this scene's terms.
   *
   * Not shared copy: a dungeon run settles as a death and burns your bag,
   * while an arena round has no loot at stake at all. Telling an arena player
   * they are about to lose everything they carry would be a lie that stops
   * them leaving.
   */
  confirmText?: string;
  /** Called whenever the menu opens or closes, so the scene can pause input handling. */
  onVisibilityChange?: (open: boolean) => void;
}

export class RunMenu {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly openBtn: HTMLButtonElement;
  private readonly keyHandler: (e: KeyboardEvent) => void;
  private confirming = false;
  private open = false;
  /** False once the run is settled: there is nothing left to abandon. */
  private available = true;

  constructor(container: HTMLElement, private readonly handlers: RunMenuHandlers, legend: string) {
    /**
     * The corner control — top right, out of the middle (2026-09-03).
     *
     * User: "the menu button is overlapping the compass at the top. We need to
     * change the button's location."
     *
     * It was `left-1/2 -translate-x-1/2` at `top-0.5rem`, and 44px tall, so it
     * covered 0.5rem to 3.25rem of the screen's centre column — which is
     * exactly where `goal-compass.ts` sits from 1rem on desktop, with the
     * target frame stacked directly under it at 3.5rem. Three things in one
     * column, and the two that matter while playing were behind the one that
     * does not.
     *
     * The centre column belongs to navigation and to what you are hitting.
     * This is the least-used control on the screen and takes the corner, which
     * is where a pause control is looked for anyway. The party frames move
     * down by its height to make room — see `combat-hud.ts`.
     *
     * 44px, because it is aimed at on a phone where there is no Escape key at
     * all.
     */
    this.openBtn = document.createElement('button');
    this.openBtn.type = 'button';
    this.openBtn.setAttribute('aria-label', t('Menu'));
    this.openBtn.className = [
      'fixed', 'z-[1400]', 'top-[max(0.5rem,env(safe-area-inset-top))]',
      'right-[max(0.75rem,env(safe-area-inset-right))]', 'min-h-11', 'min-w-11', 'px-3',
      'btn', 'btn-stone',
    ].join(' ');
    this.openBtn.textContent = t('Menu');
    this.openBtn.addEventListener('click', () => this.show());
    container.appendChild(this.openBtn);

    this.root = document.createElement('div');
    this.root.className = 'fixed inset-0 z-[1500] scrim flex items-center justify-center p-4';
    this.root.hidden = true;
    this.root.addEventListener('click', (e) => { if (e.target === this.root) this.hide(); });

    this.panel = document.createElement('div');
    this.panel.className = `frame-raised w-[min(92vw,26rem)] p-4 flex flex-col gap-3`;
    this.root.appendChild(this.panel);
    container.appendChild(this.root);

    this.legend = legend;
    this.render();

    this.keyHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape is never prevented — the browser's pointer-lock release is
      // wanted here, since the menu needs a cursor.
      if (this.open) this.hide(); else this.show();
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  private legend: string;

  get isOpen(): boolean { return this.open; }

  show(): void {
    if (this.open) return;
    if (!this.available) return;
    this.open = true;
    this.confirming = false;
    this.root.hidden = false;
    this.openBtn.hidden = true;
    this.render();
    this.handlers.onVisibilityChange?.(true);
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.confirming = false;
    this.root.hidden = true;
    // Not unconditionally visible again: closing the menu after the run has
    // settled must not put the button back. Found by a test.
    this.openBtn.hidden = !this.available;
    this.handlers.onVisibilityChange?.(false);
  }

  /** Hides the whole thing — used once the run is over and there is nothing left to abandon. */
  setAvailable(available: boolean): void {
    this.available = available;
    if (!available) this.hide();
    this.openBtn.hidden = !available || this.open;
  }

  private render(): void {
    this.panel.innerHTML = `
      <p class="font-heading text-lg ${LABEL}">${escapeHtml(t('Paused'))}</p>
      <p class="text-[12px] text-text-dim leading-snug">${escapeHtml(this.legend)}</p>
      <div class="flex flex-col gap-2 pt-1">
        <button data-run-menu="resume" class="btn btn-gold px-4">${escapeHtml(t('Resume'))}</button>
        ${this.confirming ? `
          <p class="notice-error text-[12px] leading-snug">
            ${escapeHtml(this.handlers.confirmText ?? t(DEFAULT_CONFIRM))}
          </p>
          <div class="flex gap-2">
            <button data-run-menu="cancel" class="btn btn-stone flex-1">${escapeHtml(t('Keep playing'))}</button>
            <button data-run-menu="confirm" class="btn btn-danger flex-1">${escapeHtml(t('Abandon run'))}</button>
          </div>
        ` : `
          <button data-run-menu="abandon" class="btn btn-danger px-4">${escapeHtml(t('Abandon run'))}</button>
        `}
      </div>
    `;
    this.panel.querySelector('[data-run-menu="resume"]')?.addEventListener('click', () => this.hide());
    this.panel.querySelector('[data-run-menu="abandon"]')?.addEventListener('click', () => {
      this.confirming = true;
      this.render();
    });
    this.panel.querySelector('[data-run-menu="cancel"]')?.addEventListener('click', () => {
      this.confirming = false;
      this.render();
    });
    this.panel.querySelector('[data-run-menu="confirm"]')?.addEventListener('click', () => {
      this.hide();
      this.handlers.onAbandon();
    });
  }

  dispose(): void {
    window.removeEventListener('keydown', this.keyHandler);
    this.root.remove();
    this.openBtn.remove();
  }
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}
